import React, { useContext } from 'react';
import Img from 'gatsby-image';
import styled from 'styled-components';
import LocaleLink from './LocaleLink';
import { tr } from '../utils/translations';
import LocaleContext from './LocaleContext';
import { breakpoints } from '../styles/Variables';

const JournalPagesPreviewStyles = styled.div`
  background-color: var(--tan);
  padding-top: 2rem;
  margin: 7rem 0 0 0;
  text-align: center;

  h3 {
    padding: 2rem var(--col1) 0 var(--col1);

    @media (min-width: ${breakpoints.breakMd}) {
      font-size: var(--fsTitle2);
    }
  }

  .content {
    overflow-x: auto;

    @media (min-width: ${breakpoints.breakMd}) {
      overflow-x: visible;
    }
  }

  .pagesWrapper {
    display: flex;
    padding-top: 1rem;
    padding-bottom: 2rem;

    p:not(:first-of-type) {
      display: none;
    }

    @media (min-width: ${breakpoints.breakMd}) {
      display: grid;
      grid-template-columns: 1fr 1fr;
      padding-left: var(--col1);
      padding-right: var(--col1);
      padding-bottom: 0;
      column-gap: 2rem;
    }

    @media (min-width: ${breakpoints.breakLg}) {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  .page {
    width: var(--col9);
    padding-right: var(--col1);
    flex-shrink: 0;
    text-align: left;

    &:first-child {
      margin-left: var(--col2);
    }

    .gatsby-image-wrapper {
      height: 42vw;
    }

    p {
      margin-top: 0;
    }

    .title {
      font-size: var(--fsQuote);
    }

    @media (min-width: ${breakpoints.breakMd}) {
      padding-bottom: 4rem;
      padding-right: 0;
      width: 100%;

      &:first-child {
        margin-left: 0;
      }

      .gatsby-image-wrapper {
        width: 100%;
        height: 25vw;
      }
    }

    @media (min-width: ${breakpoints.breakLg}) {
      .gatsby-image-wrapper {
        height: 15vw;
        margin-bottom: 1rem;
      }
    }
  }

  .gatsby-image-wrapper {
    margin-bottom: 1rem;
  }

  .button.cta {
    background-color: var(--black);
    color: var(--tan);
    font-size: var(--fsButton);

    &:hover {
      background-color: var(--tan);
      border-color: var(--black);
      color: var(--black);
    }
  }
`;

export default function JournalPagesPreview({ pages, currentPageId }) {
  const [locale] = useContext(LocaleContext);
  const totalPages = 3;

  const [currentPage] = pages.nodes.filter(
    (aPage) => currentPageId === aPage.id
  );
  const currentPageSlugs = currentPage.categories.map(
    (category) => category.slug
  );

  const pagesInSameCategory = pages.nodes.filter(
    // Pick all pages that share at least one category
    // with current page, but exclude current page
    (aPage) => {
      const aPageSlugs = aPage.categories.map((category) => category.slug);
      const slugsInCommon = aPageSlugs.filter((slug) =>
        currentPageSlugs.includes(slug)
      );
      return currentPageId !== aPage.id && slugsInCommon.length;
    }
  );

  const otherPages = pages.nodes
    .filter(
      (aPage) =>
        // Exclude current page
        currentPageId !== aPage.id &&
        // Exclude pages already selected based on category
        !pagesInSameCategory.filter((aCatPage) => aCatPage.id === aPage.id)
          .length
    )
    // Pick as many pages are needed as to make up totalPages
    .slice(0, totalPages - pagesInSameCategory.length);

  // Pages are ordered from newest to oldest, with the exception of
  // initial pages from same category.
  const pagesToShow = pagesInSameCategory.reverse().concat(otherPages);

  return (
    <JournalPagesPreviewStyles role="navigation">
      <h3>{tr('titles', 'moreFromTheJournal', locale)}</h3>
      <div className="content">
        <div className="pagesWrapper">
          {pagesToShow.map((aPage) => {
            // Preview image
            const anImage = aPage.heroImage || aPage.metaImage || null;
            // Journal category
            const [aCategory] = aPage.categories;
            return (
              <div className="page" key={aPage.id}>
                <LocaleLink to={`/${aPage.slug}`} className="title">
                  {anImage && (
                    <Img fluid={anImage.asset.fluid} alt={aPage.name} />
                  )}
                  {aCategory.name.toUpperCase()}: {aPage.name}
                </LocaleLink>
                <p>{aPage.introduction}</p>
                <LocaleLink to={`/${aPage.slug}`} className="button cta">
                  {tr('cta', 'viewMore', locale)}
                </LocaleLink>
              </div>
            );
          })}
        </div>
      </div>
    </JournalPagesPreviewStyles>
  );
}
